import { useState, useEffect, useCallback, useMemo } from "react"
import { cn } from "@/lib/utils"

const GLITCH_CHARS = "░▒▓█▄▀■□▪▫▬▮▯⠁⠃⠇⠏⠟⠿⡿⣿⢿⡷⠶⠴⠲⠰⠘⠊⠉"

interface ForgeLoaderProps {
  /** Full-screen loader (h-screen) vs inline/contained loader */
  fullScreen?: boolean
  /** Custom className for the container */
  className?: string
}

/**
 * Forge branded loading screen with glitch-style flickering characters
 * surrounding the "FORGE" wordmark.
 *
 * Use fullScreen={true} for page-level loading (default).
 * Use fullScreen={false} for card/section-level loading.
 */
export function ForgeLoader({ fullScreen = true, className }: ForgeLoaderProps) {
  const gridSize = fullScreen ? 30 : 16
  const [chars, setChars] = useState(() =>
    Array.from({ length: gridSize }, () => GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)])
  )

  const positions = useMemo(() =>
    Array.from({ length: gridSize }, () => ({
      x: (Math.random() - 0.5) * (fullScreen ? 280 : 160),
      y: (Math.random() - 0.5) * (fullScreen ? 160 : 80),
      size: fullScreen ? 10 + Math.random() * 14 : 8 + Math.random() * 10,
      baseOpacity: 0.06 + Math.random() * 0.15,
    })),
  [fullScreen, gridSize])

  const tick = useCallback(() => {
    setChars(prev => prev.map(c =>
      Math.random() < 0.4
        ? GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)]
        : c
    ))
  }, [])

  useEffect(() => {
    const id = setInterval(tick, 70)
    return () => clearInterval(id)
  }, [tick])

  return (
    <div className={cn(
      "flex items-center justify-center bg-background relative overflow-hidden",
      fullScreen ? "h-screen" : "py-12",
      className,
    )}>
      {chars.map((char, i) => (
        <span
          key={i}
          className="absolute font-mono text-primary select-none pointer-events-none transition-opacity duration-75"
          style={{
            transform: `translate(${positions[i].x}px, ${positions[i].y}px)`,
            fontSize: `${positions[i].size}px`,
            opacity: positions[i].baseOpacity,
          }}
        >
          {char}
        </span>
      ))}

      <div className={cn(
        "relative z-10 font-mono font-bold tracking-[0.35em] text-foreground",
        fullScreen ? "text-xl" : "text-base",
      )}>
        FORGE
      </div>
    </div>
  )
}
